import React, { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import TicketPreview from "./TicketPreview";
import SectionControls from "./SectionControls";
import ColorInput from "./ColorInput";
import GradientEditor from "./GradientEditor";
import NumberInput from "./NumberInput";
import LiveSelect from "./LiveSelect";
import { toast } from "react-toastify";
import { WEIGHT_OPTIONS, FONT_OPTIONS } from "../../utils/fonts";
import { ensureDesignerFontsLoaded } from "../../utils/loadFonts";
import { getDefaultTicketDesign, getEventTicketClassesWithDesign, getTicketClassFormFields, getTicketDesignForCopy } from "../../services/ticketDesignApi";
import CertificateOnboardingCards from "./CertificateOnboarding";
import { getDefaultGroupTicketDesign } from "../../services/groupTicketDesignApi";
import { getDashboardContextValue } from "../../utils/dashboardContext";
import {
  ArrowLeft,
  Settings,
  Image as ImageIcon,
  Type,
  Layout,
  Maximize,
  Save,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  RefreshCw,
  Plus,
  Minus,
  Trash2,
  Square,
  RotateCcw,
  AlertTriangle,
  Award,
  X,
  Grid3X3,
} from "lucide-react";

const DesignerModal = ({
  isOpen,
  onClose,
  title,
  message,
  icon,
  iconClassName = "bg-orange-50 text-orange-500",
  children,
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl overflow-hidden transform animate-in zoom-in-95 duration-200 border border-gray-100">
        <div className="p-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-xl font-bold text-gray-900">{title}</h3>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full text-gray-400 transition-colors"
            >
              <X size={20} />
            </button>
          </div>
          <p className="text-gray-500 leading-relaxed">{message}</p>
        </div>

        <div className="bg-gray-50 p-6">{children}</div>
      </div>
    </div>
  );
};

const TicketClassDropdown = ({ options, value, onChange, disabled }) => {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);

  React.useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const selected = options.find((o) => o.value === value);

  return (
    <div className="relative flex-1" ref={ref}>
      <button
        type="button"
        onClick={() => !disabled && setOpen((o) => !o)}
        disabled={disabled}
        className="w-full flex items-center justify-between px-3 py-[9px] text-sm bg-white border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-400 transition-all hover:bg-gray-50 text-left disabled:opacity-60"
      >
        <span className={`truncate ${selected ? "text-gray-800 font-medium" : "text-gray-400"}`}>
          {selected ? selected.label : "Select a ticket class..."}
        </span>
        <ChevronDown size={16} className={`text-gray-400 flex-shrink-0 ml-2 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute z-[200] left-0 right-0 bottom-full mb-1 bg-white border border-gray-200 rounded-xl shadow-xl max-h-48 overflow-y-auto py-1">
          {options.map((opt) => (
            <div
              key={opt.value}
              onClick={() => { onChange(opt.value); setOpen(false); }}
              className={`px-3 py-2 text-sm cursor-pointer flex items-center justify-between transition-colors ${
                value === opt.value ? "bg-emerald-50 text-emerald-700 font-semibold" : "text-gray-700 hover:bg-gray-50"
              }`}
            >
              <span className="truncate">{opt.label}</span>
              {value === opt.value && (
                <svg className="w-4 h-4 text-emerald-600 flex-shrink-0 ml-2" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const ALLOWED_FORM_FIELD_TYPES = new Set(["TEXT", "NUMBER", "EMAIL", "DATE", "PARAGRAPH", "TEXTAREA"]);

const getFieldType = (field) =>
  String(field?.field_type || field?.type || "").trim().toUpperCase();

const getFieldName = (field) =>
  String(field?.field_name || field?.name || field?.label || "").trim();

const ResetTemplateModal = ({
  isOpen,
  onClose,
  onResetToDefault,
  onResetToLastSave,
  onCopyFromClass,
  isSaving,
  showCopyOption = false,
  otherTicketClasses = [],
  loadingClasses = false,
  tag = "withoutProfilePic",
}) => {
  const [selectedClassId, setSelectedClassId] = React.useState("");
  const templateLabel =
    tag === "withProfilePic"
      ? "With Profile Pic"
      : tag === "forPrint"
      ? "For Print"
      : tag === "badge"
      ? "Badge"
      : tag === "certificate"
      ? "Certificate"
      : tag === "forCertificatePrint"
      ? "For Certificate Print"
      : "Without Profile Pic";

  React.useEffect(() => {
    if (!isOpen) setSelectedClassId("");
  }, [isOpen]);

  return (
    <DesignerModal
      isOpen={isOpen}
      onClose={onClose}
      title="Reset Template"
      message="Choose how you want to reset this template. Confirming a reset will also save that version to the database."
      icon={<RotateCcw size={24} />}
      iconClassName="bg-amber-50 text-amber-500"
    >
      <div className="flex flex-col gap-3">
        <button
          type="button"
          onClick={onResetToDefault}
          disabled={isSaving}
          className="w-full rounded-2xl border border-indigo-200 bg-white p-4 text-left transition-all hover:border-indigo-300 hover:bg-indigo-50 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          <div className="flex items-start gap-3">
            <div className="mt-0.5 flex h-10 w-10 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600">
              <RotateCcw size={18} />
            </div>
            <div className="flex-1">
              <div className="text-sm font-semibold text-gray-900">Reset to Default</div>
              <div className="mt-1 text-xs leading-relaxed text-gray-500">
                Replace the current layout with the default template and save it immediately.
              </div>
            </div>
          </div>
        </button>

        <button
          type="button"
          onClick={onResetToLastSave}
          disabled={isSaving}
          className="w-full rounded-2xl border border-gray-200 bg-white p-4 text-left transition-all hover:border-gray-300 hover:bg-gray-100 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          <div className="flex items-start gap-3">
            <div className="mt-0.5 flex h-10 w-10 items-center justify-center rounded-xl bg-gray-100 text-gray-600">
              <Save size={18} />
            </div>
            <div className="flex-1">
              <div className="text-sm font-semibold text-gray-900">Reset to Last Save</div>
              <div className="mt-1 text-xs leading-relaxed text-gray-500">
                Bring back the last saved version of this template from the current session and save it again.
              </div>
            </div>
          </div>
        </button>

        {showCopyOption && (
          <div className="rounded-2xl border border-emerald-200 bg-white p-4">
            <div className="flex items-start gap-3 mb-3">
              <div className="mt-0.5 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600">
                <RefreshCw size={18} />
              </div>
              <div className="flex-1">
                <div className="text-sm font-semibold text-gray-900">Copy from Another Ticket Class</div>
                <div className="mt-1 text-xs leading-relaxed text-gray-500">
                  Copy the <span className="font-medium">{templateLabel}</span> design from another ticket class in this event.
                </div>
              </div>
            </div>
            {loadingClasses ? (
              <div className="text-xs text-gray-400 py-2 text-center">Loading ticket classes...</div>
            ) : otherTicketClasses.length === 0 ? (
              <div className="text-xs text-gray-400 py-2 text-center">No other ticket classes with a saved design found.</div>
            ) : (
              <div className="flex gap-2 mt-1 items-start">
                <TicketClassDropdown
                  options={otherTicketClasses.map((tc) => ({ value: tc.ticketClassId, label: tc.name }))}
                  value={selectedClassId}
                  onChange={setSelectedClassId}
                  disabled={isSaving}
                />
                <button
                  type="button"
                  onClick={() => selectedClassId && onCopyFromClass(selectedClassId)}
                  disabled={!selectedClassId || isSaving}
                  className="rounded-xl bg-emerald-600 px-4 py-[9px] text-sm font-semibold text-white transition-all hover:bg-emerald-700 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                >
                  {isSaving ? "Copying..." : "Copy"}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </DesignerModal>
  );
};

const TemplateSwitchModal = ({
  isOpen,
  onClose,
  onSaveAndSwitch,
  onDiscardAndSwitch,
  currentTemplateLabel,
  nextTemplateLabel,
  isSaving,
}) => (
  <DesignerModal
    isOpen={isOpen}
    onClose={onClose}
    title="Save Changes Before Switching?"
    message={`You have unsaved changes in ${currentTemplateLabel}. Choose what to do before opening ${nextTemplateLabel}.`}
    icon={<AlertTriangle size={24} />}
    iconClassName="bg-orange-50 text-orange-500"
  >
    <div className="flex flex-col gap-3">
      <div className="rounded-2xl border border-orange-100 bg-orange-50 px-4 py-3 text-xs leading-relaxed text-orange-700">
        Saving will keep your updates in the database before the other template
        opens.
      </div>
      <div className="flex flex-col gap-2 sm:flex-row">
        <button
          type="button"
          onClick={onClose}
          disabled={isSaving}
          className="flex-1 rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 transition-all hover:bg-gray-100 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          Stay Here
        </button>
        <button
          type="button"
          onClick={onDiscardAndSwitch}
          disabled={isSaving}
          className="flex-1 rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 transition-all hover:bg-gray-100 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          Discard & Switch
        </button>
        <button
          type="button"
          onClick={onSaveAndSwitch}
          disabled={isSaving}
          className="flex-1 rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white transition-all hover:bg-indigo-700 shadow-lg shadow-indigo-200 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {isSaving ? "Saving..." : "Save & Switch"}
        </button>
      </div>
    </div>
  </DesignerModal>
);

const AccordionItem = ({
  id,
  title,
  icon: Icon,
  children,
  openPanels,
  togglePanel,
}) => (
  <div className="border-b border-gray-100 last:border-b-0">
    <button
      className="w-full flex items-center justify-between py-3 px-4 bg-white hover:bg-gray-50 transition-colors"
      onClick={() => togglePanel(id)}
    >
      <div className="flex items-center gap-3 text-sm font-medium text-gray-700">
        {Icon && <Icon size={16} className="text-gray-400" />}
        {title}
      </div>
      {openPanels[id] ? (
        <ChevronUp size={16} className="text-gray-400" />
      ) : (
        <ChevronDown size={16} className="text-gray-400" />
      )}
    </button>
    {openPanels[id] && (
      <div className="px-4 pb-4 pt-1 bg-white">{children}</div>
    )}
  </div>
);

export default function DesignerUI({
  title,
  design,
  initialDesign,
  setDesign,
  data,
  setData,
  isSaving,
  onSave,
  onBack,
  hidePairHeading: hidePairHeadingProp = false,
  tag = "withoutProfilePic",
  setTag,
  preGeneratePrintPdf = false,
  setPreGeneratePrintPdf,
  initialPreGeneratePrintPdf,
  readOnly = false,
  readOnlyMessage = "This ticket is processing. You can view it, but editing is locked for now.",
  readOnlyLoading = false,
  processingProgress = null,
  processingTitle = "Processing",
  lockedCategories = {},
  onCertificatePickImage,
  onCertificatePickEmpty,
  replaceDesign,
  eventId,
  ticketClassId,
  hasCertificate = true,
}) {
  const hidePairHeading = design?.meta?.hidePairHeading ?? hidePairHeadingProp;
  const isCertificateConfigured = Boolean(hasCertificate);
  const showCertificateNotSet =
    tag === "forCertificatePrint" && (!isCertificateConfigured || !design);
  const showCertificateOnboarding =
    !design && tag === "certificate";

  useEffect(() => {
    ensureDesignerFontsLoaded();
  }, []);
  const [activeTab, setActiveTab] = useState("design");
  const [selectedSection, setSelectedSection] = useState("canvas");
  const [sectionMeasurements, setSectionMeasurements] = useState({});
  const [zoom, setZoom] = useState(100);
  useEffect(() => {
    if (tag === "certificate") {
      setZoom(75);
    }
  }, [tag]);

  const [showGrid, setShowGrid] = useState(false);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (["INPUT", "TEXTAREA"].includes(document.activeElement?.tagName)) return;
      if (e.key === "g" || e.key === "G") {
        setShowGrid((prev) => !prev);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);
  const canvasViewportRef = useRef(null);
  const panStateRef = useRef({
    active: false,
    startX: 0,
    startY: 0,
    scrollLeft: 0,
    scrollTop: 0,
  });
  const [isCanvasPanning, setIsCanvasPanning] = useState(false);
  const [canvasUnit, setCanvasUnit] = useState(() => (tag === "badge" ? "mm" : "px"));
  useEffect(() => {
    if (tag === "badge") {
      setCanvasUnit("mm");
    }
  }, [tag]);
  const [canvasWidthDraft, setCanvasWidthDraft] = useState("");
  const [canvasHeightDraft, setCanvasHeightDraft] = useState("");
  const [isEditingCanvasWidth, setIsEditingCanvasWidth] = useState(false);
  const [isEditingCanvasHeight, setIsEditingCanvasHeight] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [showTemplateChangeModal, setShowTemplateChangeModal] = useState(false);
  const [pendingTag, setPendingTag] = useState(null);
  const [showPrintableView, setShowPrintableView] = useState(false);
  const [otherTicketClasses, setOtherTicketClasses] = useState([]);
  const [loadingClasses, setLoadingClasses] = useState(false);
  const [backgroundCropOpen, setBackgroundCropOpen] = useState(false);
  const [backgroundRawImage, setBackgroundRawImage] = useState(null);
  const [backgroundImgElement, setBackgroundImgElement] = useState(null);
  const [backgroundImgSize, setBackgroundImgSize] = useState({ w: 0, h: 0 });
  const [backgroundBaseScale, setBackgroundBaseScale] = useState(1);
  const [backgroundScale, setBackgroundScale] = useState(1);
  const [backgroundOffset, setBackgroundOffset] = useState({ x: 0, y: 0 });
  const [backgroundPanning, setBackgroundPanning] = useState(false);
  const [backgroundPanStart, setBackgroundPanStart] = useState({ x: 0, y: 0 });
  const [backgroundImgStart, setBackgroundImgStart] = useState({ x: 0, y: 0 });
  const backgroundCropBoxRef = useRef(null);

  useEffect(() => {
    if (readOnly && backgroundCropOpen) {
      setBackgroundCropOpen(false);
    }
  }, [readOnly, backgroundCropOpen]);

  const handleSectionMeasure = useCallback((id, size) => {
    setSectionMeasurements((prev) => {
      const current = prev[id];
      if (
        current?.width === size.width &&
        current?.height === size.height
      ) {
        return prev;
      }

      return { ...prev, [id]: size };
    });
  }, []);

  const PX_PER = { px: 1, in: 96, cm: 37.7953, mm: 3.77953 };
  const UNIT_DECIMALS = { px: 0, in: 2, cm: 2, mm: 1 };
  const toUnit = (px) =>
    +(px / PX_PER[canvasUnit]).toFixed(UNIT_DECIMALS[canvasUnit]);
  const toPx = (val) => Math.round(val * PX_PER[canvasUnit]);

  const CANVAS_PRESETS = [
    { label: "Custom", w: null, h: null },
    { label: "Badge / Sunmi (64 × 64 mm)", w: 242, h: 242 },
    { label: "Credit Card (85.6 × 53.9 mm)", w: 323, h: 204 },
    { label: "Business Card (90 × 55 mm)", w: 340, h: 208 },
    { label: "A6 Ticket (148 × 105 mm)", w: 559, h: 397 },
    { label: "Event Badge (102 × 76 mm)", w: 385, h: 287 },
    { label: "Square (100 × 100 mm)", w: 378, h: 378 },
  ];

  const [openPanels, setOpenPanels] = useState({
    userInfo: false,
    canvasConfig: false,
    background: false,
    header: false,
    templateSettings: true,
  });

  const togglePanel = (panel) => {
    setOpenPanels((prev) => {
      if (prev[panel]) return { ...prev, [panel]: false };
      const next = Object.keys(prev).reduce(
        (acc, key) => ({ ...acc, [key]: false }),
        {},
      );
      next[panel] = true;
      return next;
    });
  };

  const zoomScale = useMemo(() => zoom / 100, [zoom]);
  const previewBaseWidth = useMemo(
    () => Math.max(100, Number(design?.canvas?.width || 900)),
    [design?.canvas?.width],
  );
  const previewBaseHeight = useMemo(
    () => Math.max(100, Number(design?.canvas?.height || 1200)),
    [design?.canvas?.height],
  );
  const scaledPreviewWidth = useMemo(
    () => Math.max(100, Math.round(previewBaseWidth * zoomScale)),
    [previewBaseWidth, zoomScale],
  );
  const scaledPreviewHeight = useMemo(
    () => Math.max(100, Math.round(previewBaseHeight * zoomScale)),
    [previewBaseHeight, zoomScale],
  );

  const handleCanvasMouseDown = useCallback(
    (event) => {
      if (zoom <= 100 || event.button !== 0) {
        return;
      }
      const viewport = canvasViewportRef.current;
      if (!viewport) return;

      panStateRef.current = {
        active: true,
        startX: event.clientX,
        startY: event.clientY,
        scrollLeft: viewport.scrollLeft,
        scrollTop: viewport.scrollTop,
      };
      setIsCanvasPanning(true);
    },
    [zoom],
  );

  const handleCanvasMouseMove = useCallback((event) => {
    if (!panStateRef.current.active) return;
    const viewport = canvasViewportRef.current;
    if (!viewport) return;

    const deltaX = event.clientX - panStateRef.current.startX;
    const deltaY = event.clientY - panStateRef.current.startY;
    viewport.scrollLeft = panStateRef.current.scrollLeft - deltaX;
    viewport.scrollTop = panStateRef.current.scrollTop - deltaY;
  }, []);

  const stopCanvasPanning = useCallback(() => {
    if (!panStateRef.current.active) return;
    panStateRef.current.active = false;
    setIsCanvasPanning(false);
  }, []);

  const [showAddMenu, setShowAddMenu] = useState(false);
  const [showFormFieldPicker, setShowFormFieldPicker] = useState(false);
  const [formFields, setFormFields] = useState([]);
  const [formFieldsLoading, setFormFieldsLoading] = useState(false);
  const [formFieldSearch, setFormFieldSearch] = useState("");
  const canAddFormFields =
    title !== "Group Ticket Designer" && Boolean(eventId && ticketClassId);

  useEffect(() => {
    if (!showFormFieldPicker || !canAddFormFields) return;

    let cancelled = false;
    setFormFieldsLoading(true);

    getTicketClassFormFields(eventId, ticketClassId)
      .then((fields) => {
        if (cancelled) return;
        const allowedFields = (Array.isArray(fields) ? fields : []).filter(
          (field) =>
            getFieldName(field) &&
            ALLOWED_FORM_FIELD_TYPES.has(getFieldType(field)),
        );
        setFormFields(allowedFields);
      })
      .catch((error) => {
        if (cancelled) return;
        console.error("Failed to load ticket class form fields:", error);
        setFormFields([]);
      })
      .finally(() => {
        if (!cancelled) setFormFieldsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [canAddFormFields, eventId, showFormFieldPicker, ticketClassId]);

  const filteredFormFields = useMemo(() => {
    const query = formFieldSearch.trim().toLowerCase();
    if (!query) return formFields;
    return formFields.filter((field) =>
      [getFieldName(field), field?.field_key, getFieldType(field)]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query)),
    );
  }, [formFieldSearch, formFields]);

  useEffect(() => {
    if (!design?.canvas) return;
    if (!isEditingCanvasWidth) {
      setCanvasWidthDraft(String(toUnit(design.canvas.width)));
    }
    if (!isEditingCanvasHeight) {
      setCanvasHeightDraft(String(toUnit(design.canvas.height)));
    }
  }, [
    design?.canvas?.width,
    design?.canvas?.height,
    canvasUnit,
    isEditingCanvasWidth,
    isEditingCanvasHeight,
  ]);

  const isModified = useMemo(() => {
    if (!design && !initialDesign) return false;
    if (Boolean(design) !== Boolean(initialDesign)) return true;
    const preGenerateChanged =
      initialPreGeneratePrintPdf !== undefined &&
      (tag === "forPrint" || tag === "badge" || tag === "certificate" || tag === "forCertificatePrint") &&
      Boolean(preGeneratePrintPdf) !== Boolean(initialPreGeneratePrintPdf);
    if (preGenerateChanged) return true;
    const cleanDeep = (obj) => {
      if (!obj) return null;
      const { meta, key, canvas, ...rest } = obj;
      const normalizedCanvas = canvas
        ? {
            ...canvas,
            padding: 0,
            borderWidth: 1,
          }
        : canvas;
      return JSON.parse(JSON.stringify({ ...rest, canvas: normalizedCanvas }));
    };
    return (
      JSON.stringify(cleanDeep(design)) !==
      JSON.stringify(cleanDeep(initialDesign))
    );
  }, [design, initialDesign, initialPreGeneratePrintPdf, preGeneratePrintPdf, tag]);

  const cloneDesign = useCallback(
    (value) => (value ? JSON.parse(JSON.stringify(value)) : null),
    [],
  );

  const templateOptions = useMemo(
    () =>
      title === "Group Ticket Designer"
        ? [{ id: "default", label: "Default Template" }]
        : [
            { id: "withProfilePic", label: "With Profile Pic" },
            { id: "withoutProfilePic", label: "Without Profile Pic" },
            { id: "forPrint", label: "For Print" },
            { id: "badge", label: "Badge" },
            { id: "certificate", label: "Certificate" },
            { id: "forCertificatePrint", label: "For Certificate Print" },
          ],
    [title],
  );

  const templateGroups = useMemo(() => {
    if (title === "Group Ticket Designer") {
      return [{ label: null, hint: null, options: templateOptions }];
    }
    const byId = Object.fromEntries(templateOptions.map((o) => [o.id, o]));
    return [
      {
        label: "Ticket",
        hint: "Sent to the attendee",
        options: [
          byId.withProfilePic,
          byId.withoutProfilePic,
          byId.forPrint,
          byId.badge,
        ].filter(Boolean),
      },
      {
        label: "Certificate",
        hint: "Separate certificate PDF",
        options: [byId.certificate, byId.forCertificatePrint].filter(Boolean),
      },
    ].filter((g) => g.options.length);
  }, [templateOptions, title]);

  const getTemplateCategory = useCallback((templateTag) => {
    if (templateTag === "badge") return "badge";
    if (templateTag === "forPrint") return "print";
    if (templateTag === "certificate" || templateTag === "forCertificatePrint") return "certificate";
    return "ticket";
  }, []);

  const isTemplateLocked = useCallback(
    (templateTag) => Boolean(lockedCategories?.[getTemplateCategory(templateTag)]?.isActive),
    [getTemplateCategory, lockedCategories],
  );

  const currentTemplateLabel = useMemo(() => {
    return (
      templateOptions.find((option) =>
        title === "Group Ticket Designer"
          ? option.id === "default"
          : option.id === tag,
      )?.label || "this template"
    );
  }, [tag, templateOptions, title]);

  const pendingTemplateLabel = useMemo(() => {
    return (
      templateOptions.find((option) => option.id === pendingTag)?.label ||
      "the selected template"
    );
  }, [pendingTag, templateOptions]);

  const buildNextDesign = useCallback(
    (sourceDesign) => {
      const nextDesign = cloneDesign(sourceDesign);
      if (!nextDesign) return null;
      return {
        ...nextDesign,
        key: design?.key || tag,
        meta: { ...(nextDesign.meta || {}) },
      };
    },
    [cloneDesign, design?.key, tag],
  );

  const handleResetModalOpen = useCallback(async (e) => {
    e.stopPropagation();
    if (readOnly) return;
    setShowResetModal(true);
    if (title !== "Group Ticket Designer") {
      const eventId = getDashboardContextValue("eventId");
      const ticketId = new URLSearchParams(window.location.search).get("ticketId") ||
        window.location.pathname.split("/").pop();
      if (eventId) {
        setLoadingClasses(true);
        try {
          const classes = await getEventTicketClassesWithDesign(eventId, ticketId, tag);
          setOtherTicketClasses(classes);
        } catch (err) {
          setOtherTicketClasses([]);
        } finally {
          setLoadingClasses(false);
        }
      }
    }
  }, [readOnly, tag, title]);

  const handleReset = useCallback(
    async (resetType, sourceTicketClassId = null) => {
      let sourceDesign = null;

      if (resetType === "lastSaved") {
        sourceDesign = initialDesign;
        if (!sourceDesign) {
          toast.error("No saved template available to restore");
          return;
        }
      } else if (resetType === "copyFromClass" && sourceTicketClassId) {
        const eventId = getDashboardContextValue("eventId");
        try {
          sourceDesign = await getTicketDesignForCopy(eventId, sourceTicketClassId, tag);
        } catch (err) {
          sourceDesign = null;
        }
        if (!sourceDesign) {
          toast.error("Unable to load design from the selected ticket class");
          return;
        }
      } else {
        try {
          sourceDesign =
            title === "Group Ticket Designer"
              ? await getDefaultGroupTicketDesign()
              : await getDefaultTicketDesign(tag);
        } catch (err) {
          sourceDesign = null;
        }

        if (!sourceDesign) {
          toast.error("Unable to load default template");
          return;
        }
      }

      const nextDesign = buildNextDesign(sourceDesign);
      if (!nextDesign) {
        toast.error("Unable to reset template");
        return;
      }

      if (replaceDesign) {
        replaceDesign(nextDesign);
      } else {
        setDesign(() => nextDesign);
      }
      setSelectedSection("canvas");
      setActiveTab("design");

      const successMessage =
        resetType === "lastSaved"
          ? "Template reset to last saved version and saved to database."
          : resetType === "copyFromClass"
          ? "Ticket design saved successfully"
          : "Template reset to default and saved to database.";

      const saveOk = await onSave({
        designOverride: nextDesign,
        shouldNavigate: false,
        forceSave: true,
        successMessage,
      });

      if (saveOk) {
        setShowResetModal(false);
      }
    },
    [buildNextDesign, initialDesign, onSave, replaceDesign, setDesign, tag, title],
  );

  const handleTemplateSelection = useCallback(
    (nextTag) => {
      if (!setTag || nextTag === tag || isSaving) return;
      if (readOnly) {
        setTag(nextTag);
        return;
      }
      if (isModified) {
        setPendingTag(nextTag);
        setShowTemplateChangeModal(true);
        return;
      }
      setTag(nextTag);
    },
    [isModified, isSaving, readOnly, setTag, tag],
  );

  const closeTemplateSwitchModal = useCallback(() => {
    if (isSaving) return;
    setShowTemplateChangeModal(false);
    setPendingTag(null);
  }, [isSaving]);

  const handleDiscardAndSwitch = useCallback(() => {
    if (!setTag || !pendingTag) return;
    const nextTag = pendingTag;
    setShowTemplateChangeModal(false);
    setPendingTag(null);
    setTag(nextTag);
  }, [pendingTag, setTag]);

  const handleSaveAndSwitch = useCallback(async () => {
    if (!setTag || !pendingTag) return;
    const saveOk = await onSave({
      shouldNavigate: false,
      successMessage: `${currentTemplateLabel} saved successfully.`,
    });

    if (!saveOk) return;

    const nextTag = pendingTag;
    setShowTemplateChangeModal(false);
    setPendingTag(null);
    setTag(nextTag);
  }, [currentTemplateLabel, onSave, pendingTag, setTag]);

  const handleAddLayer = (type) => {
    if (readOnly) return;
    const id = `custom_${type}_${Date.now()}`;
    const newSection = {
      enabled: true,
      type: type,
      label: `Custom ${type}`,
      isResizable: true,
      x: 50,
      y: 50,
      width: type === "image" ? 100 : 200,
      height: type === "image" ? 100 : undefined,
    };
    if (type === "text") {
      newSection.text = "New Text";
      newSection.fontFamily = "Helvetica";
      newSection.fontSize = 14;
      newSection.lineSpacing = 0;
      newSection.wordSpacing = 0;
      newSection.letterSpacing = 0;
      newSection.color = "#000000";
      newSection.align = "center";
    }
    if (type === "shape") {
      newSection.shapeType = "rectangle";
      newSection.backgroundColor = "#cccccc";
      newSection.borderColor = "#000000";
      newSection.borderWidth = 0;
      newSection.width = 100;
      newSection.height = 100;
      newSection.borderRadius = 0;
    }
    if (type === "divider") {
      newSection.color = "#eeeeee";
      newSection.thickness = 1;
      newSection.width = 200;
      newSection.height = 1;
      newSection.spacing = 20;
    }
    setDesign((prev) => ({
      ...prev,
      sections: { ...prev.sections, [id]: newSection },
      sectionsOrder: [id, ...prev.sectionsOrder],
    }));
    setShowAddMenu(false);
    setSelectedSection(id);
    setActiveTab("properties");
  };

  const handleAddFormFieldLayer = (field) => {
    if (readOnly) return;

    const fieldName = getFieldName(field);
    const fieldType = getFieldType(field);
    if (!fieldName || !ALLOWED_FORM_FIELD_TYPES.has(fieldType)) return;

    const id = `form_field_${field.id || field.field_key || Date.now()}_${Date.now()}`;
    const newSection = {
      enabled: true,
      type: "text",
      label: fieldName,
      isFormField: true,
      fieldBinding: {
        fieldId: field.id,
        fieldName,
        fieldKey: field.field_key || field.fieldKey || "",
        fieldType,
      },
      previewText: fieldName,
      fontFamily: "Helvetica",
      fontSize: 14,
      fontWeight: 400,
      lineSpacing: 0,
      wordSpacing: 0,
      letterSpacing: 0,
      color: "#000000",
      align: "center",
      isResizable: true,
      x: 50,
      y: 50,
      width: 200,
    };

    setDesign((prev) => ({
      ...prev,
      sections: { ...prev.sections, [id]: newSection },
      sectionsOrder: [id, ...prev.sectionsOrder],
    }));
    setShowAddMenu(false);
    setShowFormFieldPicker(false);
    setFormFieldSearch("");
    setSelectedSection(id);
    setActiveTab("properties");
  };

  const toggleAllLayers = (enabled) => {
    if (readOnly) return;
    setDesign((prev) => {
      const nextSections = { ...prev.sections };
      prev.sectionsOrder.forEach((id) => {
        if (nextSections[id]) {
          nextSections[id] = { ...nextSections[id], enabled };
        }
      });
      return { ...prev, sections: nextSections };
    });
  };

  const handleRemoveLayer = (secId, e) => {
    e.stopPropagation();
    if (readOnly) return;
    setDesign((prev) => {
      const { [secId]: _, ...restSections } = prev.sections;
      return {
        ...prev,
        sections: restSections,
        sectionsOrder: prev.sectionsOrder.filter((id) => id !== secId),
      };
    });
    if (selectedSection === secId) setSelectedSection("canvas");
  };

  const backgroundCropBoxSize = useMemo(() => {
    const canvasW = Math.max(100, Number(design?.canvas?.width || 900));
    const canvasH = Math.max(100, Number(design?.canvas?.height || 1200));
    const scale = Math.min(300 / canvasW, 420 / canvasH, 1);

    return {
      width: Math.round(canvasW * scale),
      height: Math.round(canvasH * scale),
    };
  }, [design?.canvas?.height, design?.canvas?.width]);

  const backgroundCropBoxWidth = backgroundCropBoxSize.width;
  const backgroundCropBoxHeight = backgroundCropBoxSize.height;

  const canvasCornerRadii = useMemo(() => {
    const fallbackRadius = Number(design?.canvas?.borderRadius ?? 20) || 0;
    return {
      all: fallbackRadius,
      topLeft: Number(design?.canvas?.borderTopLeftRadius ?? fallbackRadius) || 0,
      topRight: Number(design?.canvas?.borderTopRightRadius ?? fallbackRadius) || 0,
      bottomRight:
        Number(design?.canvas?.borderBottomRightRadius ?? fallbackRadius) || 0,
      bottomLeft:
        Number(design?.canvas?.borderBottomLeftRadius ?? fallbackRadius) || 0,
    };
  }, [
    design?.canvas?.borderBottomLeftRadius,
    design?.canvas?.borderBottomRightRadius,
    design?.canvas?.borderRadius,
    design?.canvas?.borderTopLeftRadius,
    design?.canvas?.borderTopRightRadius,
  ]);

  const canvasBackgroundMode =
    design?.canvas?.backgroundMode ||
    (design?.canvas?.backgroundGradientFrom || design?.canvas?.backgroundGradientTo
      ? "gradient"
      : "solid");

  const canvasGradientPreview = useMemo(() => {
    const gradientFrom = design?.canvas?.backgroundGradientFrom || "#ffffff";
    const gradientTo = design?.canvas?.backgroundGradientTo || "#dbeafe";
    const gradientAngle = Number(design?.canvas?.backgroundGradientAngle ?? 135);
    return `linear-gradient(${gradientAngle}deg, ${gradientFrom}, ${gradientTo})`;
  }, [
    design?.canvas?.backgroundGradientAngle,
    design?.canvas?.backgroundGradientFrom,
    design?.canvas?.backgroundGradientTo,
  ]);

  const normalizedProcessingProgress = useMemo(() => {
    const processed = Math.max(0, Number(processingProgress?.processed || 0));
    const pending = Math.max(0, Number(processingProgress?.pending || 0));
    const explicitTotal = Math.max(0, Number(processingProgress?.total || 0));
    const total = Math.max(explicitTotal, processed + pending);
    const percent = total > 0 ? Math.min(100, Math.round((processed / total) * 100)) : 0;
    return { processed, total, pending, percent };
  }, [processingProgress]);

  const updateCanvas = useCallback(
    (patch) => {
      setDesign((prev) => {
        const currentCanvas = prev.canvas || {};
        const nextPatch = { ...patch };
        const hasIndividualRadiusUpdate = [
          "borderTopLeftRadius",
          "borderTopRightRadius",
          "borderBottomRightRadius",
          "borderBottomLeftRadius",
        ].some((key) => key in nextPatch);

        if ("borderRadius" in nextPatch && !hasIndividualRadiusUpdate) {
          nextPatch.borderTopLeftRadius = nextPatch.borderRadius;
          nextPatch.borderTopRightRadius = nextPatch.borderRadius;
          nextPatch.borderBottomRightRadius = nextPatch.borderRadius;
          nextPatch.borderBottomLeftRadius = nextPatch.borderRadius;
        }

        const nextCanvas = {
          ...currentCanvas,
          ...nextPatch,
          padding: 0,
        };
        const parsedBorderWidth = Number(nextCanvas.borderWidth);
        nextCanvas.borderWidth =
          Number.isFinite(parsedBorderWidth) && parsedBorderWidth >= 0
            ? parsedBorderWidth
            : 1;
        const radii = [
          Number(nextCanvas.borderTopLeftRadius ?? nextCanvas.borderRadius ?? 0) ||
            0,
          Number(nextCanvas.borderTopRightRadius ?? nextCanvas.borderRadius ?? 0) ||
            0,
          Number(
            nextCanvas.borderBottomRightRadius ?? nextCanvas.borderRadius ?? 0,
          ) || 0,
          Number(
            nextCanvas.borderBottomLeftRadius ?? nextCanvas.borderRadius ?? 0,
          ) || 0,
        ];
        nextCanvas.borderTopLeftRadius = radii[0];
        nextCanvas.borderTopRightRadius = radii[1];
        nextCanvas.borderBottomRightRadius = radii[2];
        nextCanvas.borderBottomLeftRadius = radii[3];
        nextCanvas.borderRadius = radii.every((value) => value === radii[0])
          ? radii[0]
          : Math.max(...radii);
        if (!nextCanvas.backgroundMode) {
          nextCanvas.backgroundMode = "solid";
        }
        if (!nextCanvas.backgroundGradientFrom) {
          nextCanvas.backgroundGradientFrom = nextCanvas.background || "#ffffff";
        }
        if (!nextCanvas.backgroundGradientTo) {
          nextCanvas.backgroundGradientTo = "#dbeafe";
        }
        if (
          !Number.isFinite(Number(nextCanvas.backgroundGradientAngle ?? undefined))
        ) {
          nextCanvas.backgroundGradientAngle = 135;
        }
        return {
          ...prev,
          canvas: nextCanvas,
        };
      });
    },
    [setDesign],
  );

  const commitCanvasDimension = useCallback(
    (field, rawValue) => {
      const parsed = Number(rawValue);
      if (Number.isNaN(parsed)) {
        if (field === "width") {
          setCanvasWidthDraft(String(toUnit(design.canvas.width)));
        } else {
          setCanvasHeightDraft(String(toUnit(design.canvas.height)));
        }
        return;
      }

      const minPx = 100;
      const maxPx = field === "width" ? 800 : 1200;
      const nextPx = Math.max(minPx, Math.min(maxPx, toPx(parsed)));

      if (field === "width") {
        updateCanvas({ width: nextPx });
        setCanvasWidthDraft(String(toUnit(nextPx)));
      } else {
        updateCanvas({ height: nextPx });
        setCanvasHeightDraft(String(toUnit(nextPx)));
      }
    },
    [design?.canvas?.width, design?.canvas?.height, toPx, toUnit, updateCanvas],
  );

  const updateCanvasDimensionLive = useCallback(
    (field, value) => {
      const parsed = Number(value);
      if (!Number.isFinite(parsed)) return;

      const minPx = 100;
      const maxPx = field === "width" ? 800 : 1200;
      const nextPx = Math.max(minPx, Math.min(maxPx, toPx(parsed)));

      updateCanvas({ [field]: nextPx });
    },
    [toPx, updateCanvas],
  );

  const updateHeader = useCallback(
    (patch) => {
      setDesign((prev) => ({ ...prev, header: { ...prev.header, ...patch } }));
    },
    [setDesign],
  );

  const toggleSection = (id, enabled) => {
    setDesign((prev) => ({
      ...prev,
      sections: { ...prev.sections, [id]: { ...prev.sections[id], enabled } },
    }));
  };

  const updateSection = useCallback(
    (id, nextOrPatch) => {
      setDesign((prev) => {
        const prevSec = prev.sections[id] || {};
        const next =
          typeof nextOrPatch === "function"
            ? nextOrPatch(prevSec)
            : { ...prevSec, ...nextOrPatch };
        return {
          ...prev,
          sections: { ...prev.sections, [id]: next },
        };
      });
    },
    [setDesign],
  );

  const onDragEnd = (result) => {
    if (!design || !result.destination) return;
    const newOrder = Array.from(design.sectionsOrder);
    const [moved] = newOrder.splice(result.source.index, 1);
    newOrder.splice(result.destination.index, 0, moved);
    setDesign({ sectionsOrder: newOrder });
  };

  const handleBackgroundImageChange = useCallback(
    (e) => {
      const file = e.target.files?.[0];
      e.target.value = "";
      if (!file) return;
      if (!["image/png", "image/jpeg"].includes(file.type)) {
        toast.error("Only JPG and PNG images are allowed");
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error("Image size should be less than 10MB");
        return;
      }
      const objectUrl = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => {
        const imageWidth = img.naturalWidth || img.width;
        const imageHeight = img.naturalHeight || img.height;
        const initialScale = Math.min(
          backgroundCropBoxWidth / imageWidth,
          backgroundCropBoxHeight / imageHeight,
        );
        setBackgroundRawImage({
          src: objectUrl,
          type: file.type,
          name: file.name,
        });
        setBackgroundImgElement(img);
        setBackgroundImgSize({ w: imageWidth, h: imageHeight });
        setBackgroundBaseScale(initialScale);
        setBackgroundScale(1);
        setBackgroundOffset({ x: 0, y: 0 });
        setBackgroundCropOpen(true);
      };
      img.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        toast.error("Unable to load image");
      };
      img.src = objectUrl;
    },
    [backgroundCropBoxHeight, backgroundCropBoxWidth],
  );

  const handleRemoveBackgroundImage = useCallback(() => {
    updateCanvas({ backgroundImage: "", backgroundImageOpacity: 0.5 });
  }, [updateCanvas]);

  const closeBackgroundCrop = useCallback(() => {
    setBackgroundCropOpen(false);
    if (backgroundRawImage?.src) {
      URL.revokeObjectURL(backgroundRawImage.src);
    }
    setBackgroundRawImage(null);
    setBackgroundImgElement(null);
    setBackgroundImgSize({ w: 0, h: 0 });
    setBackgroundBaseScale(1);
    setBackgroundScale(1);
    setBackgroundOffset({ x: 0, y: 0 });
    setBackgroundPanning(false);
    setBackgroundPanStart({ x: 0, y: 0 });
    setBackgroundImgStart({ x: 0, y: 0 });
  }, [backgroundRawImage]);

  const onBackgroundPointerDown = useCallback(
    (e) => {
      if (backgroundScale <= 1) return;
      e.preventDefault();
      setBackgroundPanning(true);
      setBackgroundPanStart({ x: e.clientX, y: e.clientY });
      setBackgroundImgStart({ x: backgroundOffset.x, y: backgroundOffset.y });
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch {}
    },
    [backgroundOffset.x, backgroundOffset.y, backgroundScale],
  );

  const onBackgroundPointerMove = useCallback(
    (e) => {
      if (!backgroundPanning) return;
      const dx = e.clientX - backgroundPanStart.x;
      const dy = e.clientY - backgroundPanStart.y;
      setBackgroundOffset({
        x: backgroundImgStart.x + dx,
        y: backgroundImgStart.y + dy,
      });
    },
    [
      backgroundImgStart.x,
      backgroundImgStart.y,
      backgroundPanStart.x,
      backgroundPanStart.y,
      backgroundPanning,
    ],
  );

  const onBackgroundPointerUp = useCallback(() => {
    setBackgroundPanning(false);
  }, []);

  const confirmBackgroundCrop = useCallback(async () => {
    if (!backgroundImgElement) return;
    const canvasW = Math.max(100, Number(design?.canvas?.width || 900));
    const canvasH = Math.max(100, Number(design?.canvas?.height || 1200));
    const cropBox = backgroundCropBoxRef.current;
    const boxW = cropBox?.clientWidth || canvasW;
    const boxH = cropBox?.clientHeight || canvasH;
    const finalScale = backgroundBaseScale * backgroundScale;
    const drawW = backgroundImgSize.w * finalScale;
    const drawH = backgroundImgSize.h * finalScale;
    const exportScale = 3;
    const exportW = Math.round(canvasW * exportScale);
    const exportH = Math.round(canvasH * exportScale);
    const exportCanvas = document.createElement("canvas");
    exportCanvas.width = exportW;
    exportCanvas.height = exportH;
    const ctx = exportCanvas.getContext("2d");
    ctx.clearRect(0, 0, exportW, exportH);
    ctx.save();
    ctx.rect(0, 0, exportW, exportH);
    ctx.clip();
    const canvasScaleX = exportW / boxW;
    const canvasScaleY = exportH / boxH;
    const dx = (boxW / 2 - drawW / 2 + backgroundOffset.x) * canvasScaleX;
    const dy = (boxH / 2 - drawH / 2 + backgroundOffset.y) * canvasScaleY;
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(
      backgroundImgElement,
      dx,
      dy,
      drawW * canvasScaleX,
      drawH * canvasScaleY,
    );
    ctx.restore();
    await new Promise((resolve) => setTimeout(resolve, 0));
    exportCanvas.toBlob(
      (blob) => {
        if (!blob) return;
        const reader = new FileReader();
        reader.onload = (event) => {
          updateCanvas({ backgroundImage: event.target.result });
          closeBackgroundCrop();
        };
        reader.readAsDataURL(blob);
      },
      backgroundRawImage?.type === "image/jpeg" ? "image/jpeg" : "image/png",
      1,
    );
  }, [
    backgroundBaseScale,
    backgroundImgElement,
    backgroundImgSize.h,
    backgroundImgSize.w,
    backgroundOffset.x,
    backgroundOffset.y,
    backgroundRawImage?.type,
    backgroundScale,
    closeBackgroundCrop,
    design?.canvas?.height,
    design?.canvas?.width,
    updateCanvas,
  ]);

  if (!design && !showCertificateOnboarding && !showCertificateNotSet) return null;

  return (
    <div className="ticket-designer flex h-[calc(100vh-64px)] w-full bg-gray-100 flex-col font-sans overflow-hidden">
      {backgroundCropOpen && (
        <div className="fixed inset-0 bg-black/60 z-[1200] flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[calc(100vh-32px)] flex flex-col overflow-hidden">
            <div className="shrink-0 px-6 py-4 border-b border-gray-100 bg-gray-50/50 flex items-center justify-between">
              <div className="text-lg font-semibold text-gray-900">
                Adjust Background
              </div>
              <button
                onClick={closeBackgroundCrop}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-all"
              >
                <X size={20} />
              </button>
            </div>
            <div className="flex-1 min-h-0 overflow-auto p-8 bg-gray-50 flex flex-col items-center">
              <div
                ref={backgroundCropBoxRef}
                className={`relative flex-shrink-0 overflow-hidden bg-white shadow-xl border border-gray-200 ${
                  backgroundScale > 1
                    ? backgroundPanning
                      ? "cursor-grabbing"
                      : "cursor-grab"
                    : "cursor-zoom-in"
                }`}
                style={{
                  width: backgroundCropBoxWidth,
                  height: backgroundCropBoxHeight,
                  touchAction: "none",
                }}
                onPointerDown={onBackgroundPointerDown}
                onPointerMove={onBackgroundPointerMove}
                onPointerUp={onBackgroundPointerUp}
                onPointerLeave={onBackgroundPointerUp}
              >
                <div className="absolute inset-0">
                  {backgroundImgElement && (
                    <img
                      src={backgroundRawImage?.src}
                      alt=""
                      draggable={false}
                      style={{
                        width: backgroundImgSize.w * backgroundBaseScale,
                        height: backgroundImgSize.h * backgroundBaseScale,
                        maxWidth: "none",
                        maxHeight: "none",
                        position: "absolute",
                        left: "50%",
                        top: "50%",
                        transform: `translate(calc(-50% + ${backgroundOffset.x}px), calc(-50% + ${backgroundOffset.y}px)) scale(${backgroundScale})`,
                        transformOrigin: "center center",
                        userSelect: "none",
                        willChange: "transform",
                        display: "block",
                      }}
                    />
                  )}
                </div>
                <div className="absolute inset-0 ring-2 ring-black/5 pointer-events-none z-10 shadow-[inset_0_0_100px_rgba(0,0,0,0.1)]"></div>
              </div>
              <div className="mt-8 w-full max-w-sm space-y-4">
                <div className="flex items-center gap-4 text-gray-400">
                  <div className="w-5 h-5 flex items-center justify-center font-bold text-xl cursor-default select-none">
                    −
                  </div>
                  <input
                    type="range"
                    min="0.5"
                    max="4"
                    step="0.01"
                    value={backgroundScale}
                    onChange={(e) => setBackgroundScale(parseFloat(e.target.value))}
                    className="flex-1 accent-blue-600 h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                  />
                  <div className="w-5 h-5 flex items-center justify-center font-bold text-xl cursor-default select-none">
                    +
                  </div>
                </div>
                <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-[0.2em] text-center">
                  Drag to move • Use slider to zoom
                </p>
              </div>
            </div>
            <div className="shrink-0 px-6 py-4 border-t border-gray-100 bg-gray-50/30 flex justify-end gap-3">
              <button
                onClick={closeBackgroundCrop}
                className="h-10 px-6 text-sm font-semibold text-gray-500 hover:text-gray-700 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-all active:scale-95"
              >
                Cancel
              </button>
              <button
                onClick={confirmBackgroundCrop}
                className="h-10 px-8 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700 shadow-lg shadow-blue-100 transition-all hover:scale-[1.02] active:scale-98"
              >
                Use Background
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="shrink-0 px-6 py-4 border-b border-gray-200 bg-white z-10 relative flex items-center justify-between shadow-sm">
        <div className="flex items-center gap-4">
          <button
            onClick={onBack}
            className="p-1.5 rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors"
            title="Go Back"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-md font-bold text-gray-900 leading-none m-0">
              {title}
            </h1>
            <p className="text-xs text-gray-500 mt-1 m-0">
              {readOnly ? "Processing mode - preview only" : "Customize your ticket layout"}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {readOnly && (
            <div className="hidden md:flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-700">
              <RefreshCw className={`h-3.5 w-3.5 ${readOnlyLoading ? "animate-spin" : ""}`} />
              Processing
            </div>
          )}
          {design &&
            (tag === "forPrint" || tag === "badge" || tag === "certificate" || tag === "forCertificatePrint") &&
            title !== "Group Ticket Designer" && (
              <button
                type="button"
                onClick={() => setPreGeneratePrintPdf?.((prev) => !prev)}
                disabled={readOnly}
                title={
                  tag === "certificate" || tag === "forCertificatePrint"
                    ? "Pre-build certificate PDFs for all existing registrations on save"
                    : tag === "badge"
                    ? "Pre-build badge PDFs for all existing registrations on save"
                    : "Pre-build print PDFs for all existing registrations on save"
                }
                className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-700 transition-all hover:bg-emerald-100 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <span>Pre-generate PDF</span>
                <span
                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                    preGeneratePrintPdf ? "bg-emerald-600" : "bg-emerald-200"
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 rounded-full bg-white shadow-sm transition-transform ${
                      preGeneratePrintPdf ? "translate-x-4" : "translate-x-0.5"
                    }`}
                  />
                </span>
              </button>
            )}
          {design && (tag === "forPrint" || tag === "badge" || tag === "forCertificatePrint") && title !== "Group Ticket Designer" && (
            <button
              type="button"
              onClick={() => setShowPrintableView((prev) => !prev)}
              className="flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-xs font-semibold text-blue-700 transition-all hover:bg-blue-100"
            >
              <span>Show Printable View</span>
              <span
                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                  showPrintableView ? "bg-blue-600" : "bg-blue-200"
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 rounded-full bg-white shadow-sm transition-transform ${
                    showPrintableView ? "translate-x-4" : "translate-x-0.5"
                  }`}
                />
              </span>
            </button>
          )}
          {!readOnly && design && (
            <button
              className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-all duration-150 border bg-indigo-600 text-white border-indigo-600 hover:bg-indigo-700 active:scale-95 shadow-sm disabled:opacity-70 disabled:cursor-not-allowed"
              onClick={onSave}
              disabled={isSaving}
            >
              {isSaving ? (
                <span className="flex items-center gap-2">
                  <RefreshCw className="w-4 h-4 animate-spin" /> Saving...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Save className="w-4 h-4" /> Save Design
                </span>
              )}
            </button>
          )}
        </div>
      </div>

      <div className="flex flex-row-reverse flex-1 overflow-hidden relative">
        {/* Canvas Area (Center) */}
        <div className="flex-1 bg-gray-50 flex flex-col relative overflow-hidden">
          {/* Zoom/View toolbar */}
          <div className="absolute top-4 right-4 z-10 flex bg-white border border-gray-200 rounded-md shadow-sm text-sm overflow-hidden h-9">
            <div className="flex items-center">
              <button
                className="px-3 text-gray-500 hover:text-gray-700 hover:bg-gray-50 cursor-pointer"
                title="Zoom Out"
                onClick={() => setZoom((z) => Math.max(25, z - 25))}
              >
                -
              </button>
              <span className="text-xs text-gray-600 font-medium px-2 w-12 text-center select-none">
                {zoom}%
              </span>
              <button
                className="px-3 text-gray-500 hover:text-gray-700 hover:bg-gray-50 cursor-pointer"
                title="Zoom In"
                onClick={() => setZoom((z) => Math.min(300, z + 25))}
              >
                +
              </button>
              <div className="w-[1px] h-5 bg-gray-200 mx-1 self-center" />
              <button
                className={`px-2.5 h-full flex items-center justify-center transition-colors cursor-pointer ${
                  showGrid
                    ? "text-blue-600 bg-blue-50 font-semibold"
                    : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                }`}
                title="Toggle Canvas Grid (G)"
                onClick={() => setShowGrid((g) => !g)}
              >
                <Grid3X3 size={15} />
              </button>
            </div>
          </div>

          <div
            ref={canvasViewportRef}
            onMouseDown={handleCanvasMouseDown}
            onMouseMove={handleCanvasMouseMove}
            onMouseUp={stopCanvasPanning}
            onMouseLeave={stopCanvasPanning}
            className={`flex-1 overflow-auto p-12 hide-scrollbar ${
              zoom > 100
                ? isCanvasPanning
                  ? "cursor-grabbing select-none"
                  : "cursor-grab"
                : ""
            }`}
          >
            <div className="min-w-full min-h-full flex items-center justify-center p-6">
              {showCertificateNotSet ? (
                <div className="flex flex-col items-center justify-center p-8 max-w-md text-center bg-white rounded-2xl shadow-xl border border-gray-200 animate-in fade-in zoom-in-95 duration-200 m-6">
                  <div className="w-16 h-16 rounded-2xl bg-amber-50 border border-amber-200 flex items-center justify-center text-amber-600 mb-4 shadow-sm">
                    <Award className="w-8 h-8" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    Please Set Certificate First
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed mb-6">
                    For Certificate Print, you need to set up the Certificate template first. Certificate Print uses your certificate as its layout foundation.
                  </p>
                  <button
                    type="button"
                    onClick={() => handleTemplateSelection("certificate")}
                    className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-semibold text-sm shadow-md shadow-indigo-100 transition-all hover:shadow-lg active:scale-95 cursor-pointer"
                  >
                    <Layout className="w-4 h-4" />
                    <span>Set Certificate</span>
                  </button>
                </div>
              ) : showCertificateOnboarding ? (
                <CertificateOnboardingCards
                  onPickImage={onCertificatePickImage}
                  onPickEmpty={onCertificatePickEmpty}
                />
              ) : (
              <div
                style={{
                  width: scaledPreviewWidth,
                  height: scaledPreviewHeight,
                }}
                className="relative shrink-0"
              >
                <div
                  style={{
                    width: previewBaseWidth,
                    height: previewBaseHeight,
                    transform: `scale(${zoomScale})`,
                    transformOrigin: "top left",
                  }}
                  className="absolute top-0 left-0 transition-transform duration-200 hover:shadow-2xl shadow-xl rounded-lg bg-white border border-gray-200"
                >
                  <TicketPreview
                    design={design}
                    data={data}
                    selectedId={selectedSection}
                    onSelect={(id) => {
                      setSelectedSection(id);
                      if (!readOnly) {
                        setActiveTab("properties"); // Auto-switch to properties on click
                      }
                    }}
                    onSectionChange={readOnly ? undefined : (id, patch) => updateSection(id, patch)}
                    onSectionMeasure={handleSectionMeasure}
                    onHeaderChange={readOnly ? undefined : (patch) => updateHeader(patch)}
                    printableView={
                      (tag === "forPrint" || tag === "badge" || tag === "forCertificatePrint") &&
                      showPrintableView
                    }
                    showGrid={showGrid}
                  />
                </div>
              </div>
              )}
            </div>
          </div>
        </div>

        <div className="w-[390px] bg-white border-r border-gray-200 flex flex-col shadow-[4px_0_15px_-3px_rgba(0,0,0,0.05)] z-20 shrink-0">
          {readOnly ? (
            <div className="border-b border-gray-200 bg-white px-4 py-3">
              <div className="text-xs font-semibold uppercase tracking-[0.18em] text-amber-600">
                Processing
              </div>
            </div>
          ) : (
            <nav
              className="flex overflow-x-auto border-b border-gray-200 bg-white shrink-0"
              style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
            >
              {[
                { id: "design", label: "Design" },
                { id: "properties", label: "Properties" },
                { id: "layers", label: "Layers" },
              ].map((t) => {
                const isActive = activeTab === t.id;
                return (
                  <button
                    key={t.id}
                    onClick={() => setActiveTab(t.id)}
                    className={`
                      relative capitalize whitespace-nowrap flex-1 py-3.5 text-sm font-medium
                      transition-colors duration-150 focus:outline-none flex-shrink-0 flex justify-center items-center gap-1.5
                      ${isActive ? "text-indigo-600" : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"}
                    `}
                  >
                    {t.label}
                    {isActive && (
                      <span className="absolute bottom-0 left-0 right-0 h-0.5 rounded-full bg-indigo-600" />
                    )}
                  </button>
                );
              })}
            </nav>
          )}

          <div className="flex-1 overflow-y-auto bg-gray-50/30 custom-scrollbar ticket-designer">
            {readOnly && (
              <div className="p-5">
                <div className="rounded-2xl border border-amber-200 bg-white p-5 shadow-sm">
                  <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-amber-50 text-amber-600">
                    <RefreshCw className={`h-5 w-5 ${readOnlyLoading ? "animate-spin" : ""}`} />
                  </div>
                  <h2 className="m-0 text-base font-bold text-gray-900">
                    {processingTitle}
                  </h2>
                  <p className="mt-2 text-sm leading-6 text-gray-600">
                    {readOnlyMessage}
                  </p>
                  <div className="mt-5 rounded-xl border border-gray-200 bg-gray-50 p-3">
                    <div className="flex items-center justify-between text-xs font-semibold text-gray-500">
                      <span>Status</span>
                      <span className="rounded-full bg-amber-100 px-2 py-1 text-amber-700">
                        In progress
                      </span>
                    </div>
                    <div className="mt-3 flex items-end justify-between gap-3">
                      <div>
                        <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-400">
                          Generated
                        </div>
                        <div className="mt-1 text-lg font-bold text-gray-900">
                          {normalizedProcessingProgress.total > 0
                            ? `${normalizedProcessingProgress.processed} / ${normalizedProcessingProgress.total}`
                            : "Preparing..."}
                        </div>
                      </div>
                      {normalizedProcessingProgress.total > 0 && (
                        <div className="text-xs font-semibold text-gray-500">
                          {normalizedProcessingProgress.pending} remaining
                        </div>
                      )}
                    </div>
                    <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-gray-200">
                      <div
                        className="h-full rounded-full bg-amber-500 transition-all duration-500"
                        style={{
                          width: normalizedProcessingProgress.total > 0
                            ? `${Math.max(4, normalizedProcessingProgress.percent)}%`
                            : "40%",
                        }}
                      />
                    </div>
                    {normalizedProcessingProgress.total > 0 && (
                      <div className="mt-2 text-right text-[11px] font-semibold text-gray-400">
                        {normalizedProcessingProgress.percent}%
                      </div>
                    )}
                  </div>
                  <div className="mt-4 text-xs leading-5 text-gray-500">
                    Editing, resetting templates, adding layers, and saving are paused only for this category.
                  </div>
                  {setTag && title !== "Group Ticket Designer" && (
                    <div className="mt-5 border-t border-gray-100 pt-4">
                      <div className="mb-3 text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-400">
                        Switch Template
                      </div>
                      <div className="flex flex-col gap-3">
                        {templateGroups.map((grp) => (
                          <div key={grp.label || "group"} className="flex flex-col gap-2">
                            {grp.label && (
                              <div className="flex items-center justify-between px-0.5">
                                <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                                  {grp.label}
                                </span>
                                {lockedCategories?.[getTemplateCategory(grp.options[0]?.id)]?.isActive && (
                                  <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-700">
                                    Processing
                                  </span>
                                )}
                              </div>
                            )}
                            {grp.options.map((t) => {
                              const isActive = tag === t.id;
                              const locked = isTemplateLocked(t.id);
                              return (
                                <button
                                  key={t.id}
                                  type="button"
                                  onClick={() => handleTemplateSelection(t.id)}
                                  disabled={isSaving || isActive}
                                  className={`flex items-center justify-between rounded-lg border px-3 py-2.5 text-left text-sm font-medium transition-all disabled:cursor-default ${
                                    isActive
                                      ? "border-indigo-200 bg-indigo-50 text-indigo-700"
                                      : "border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:bg-gray-50"
                                  }`}
                                >
                                  <span>{t.label}</span>
                                  <div className="flex items-center gap-1.5">
                                    {t.id === "forCertificatePrint" && !isCertificateConfigured && (
                                      <span className="rounded-full bg-amber-50 border border-amber-200 px-2 py-0.5 text-[10px] font-semibold text-amber-700">
                                        Needs Certificate
                                      </span>
                                    )}
                                    {locked && !isActive && (
                                      <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-bold text-amber-600">
                                        Locked
                                      </span>
                                    )}
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
            {!readOnly && activeTab === "design" && (
              <div className="flex flex-col">
                <div className="text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3 bg-gray-50 border-b border-gray-100 flex items-center justify-between">
                  Ticket Components
                </div>

                {(setTag || title === "Group Ticket Designer") && (
                  <AccordionItem
                    id="templateSettings"
                    title="Template"
                    icon={Layout}
                    openPanels={openPanels}
                    togglePanel={togglePanel}
                  >
                    <div className="flex flex-col gap-3">
                      <p className="text-[11px] text-gray-500 mb-1 leading-relaxed">
                        {title === "Group Ticket Designer"
                          ? "Pre-registration uses a single default template."
                          : "Choose which ticket template to design. Different layouts can be used depending on whether the attendee has a profile picture."}
                      </p>
                      <div className="flex flex-col gap-4">
                        {templateGroups.map((grp) => (
                          <div
                            key={grp.label || "group"}
                            className="flex flex-col gap-2"
                          >
                            {grp.label && (
                              <div className="flex items-baseline justify-between px-0.5">
                                <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                                  {grp.label}
                                </span>
                                {grp.hint && (
                                  <span className="text-[10px] text-gray-300">
                                    {grp.hint}
                                  </span>
                                )}
                              </div>
                            )}
                            {grp.options.map((t) => {
                              const isActive =
                                title === "Group Ticket Designer"
                                  ? true
                                  : tag === t.id;
                              const locked = isTemplateLocked(t.id);
                              return (
                                <div
                                  key={t.id}
                                  className={`
                                 group flex items-center justify-between px-3 py-2.5 rounded-lg border text-sm font-medium transition-all
                                 ${
                                   isActive
                                     ? "bg-indigo-50 border-indigo-200 text-indigo-700 ring-2 ring-indigo-500/10"
                                     : "bg-white border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50"
                                 }
                               `}
                                >
                                  <button
                                    type="button"
                                    onClick={() => handleTemplateSelection(t.id)}
                                    className="flex flex-1 items-center gap-2 text-left"
                                  >
                                    <span>{t.label}</span>
                                    {t.id === "forCertificatePrint" && !isCertificateConfigured && (
                                      <span className="flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-amber-50 border border-amber-200 text-amber-700 text-[10px] font-semibold tracking-tight">
                                        Needs Certificate
                                      </span>
                                    )}
                                    {locked && (
                                      <span className="flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-600 text-[10px] font-bold tracking-tight">
                                        LOCKED
                                      </span>
                                    )}
                                    {isActive && isModified && (
                                      <span className="flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-orange-100 text-orange-600 text-[10px] font-bold tracking-tight animate-pulse">
                                        MODIFIED
                                      </span>
                                    )}
                                  </button>
                                  <div className="flex items-center gap-2">
                                    {isActive && (
                                      <button
                                        type="button"
                                        onClick={handleResetModalOpen}
                                        disabled={locked}
                                        className="p-1 px-1.5 hover:bg-white rounded border border-indigo-200 text-indigo-400 hover:text-indigo-600 transition-colors flex items-center gap-1 text-[10px]"
                                        title="Reset this template"
                                      >
                                        <RotateCcw size={12} />
                                        Reset
                                      </button>
                                    )}
                                    {isActive && (
                                      <div className="w-1.5 h-1.5 rounded-full bg-indigo-600 shadow-[0_0_8px_rgba(79,70,229,0.5)]"></div>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        ))}
                      </div>
                    </div>
                  </AccordionItem>
                )}

                {showCertificateNotSet && (
                  <div className="m-4 p-4 rounded-xl bg-amber-50 border border-amber-200 text-amber-900 shadow-sm animate-in fade-in duration-200">
                    <div className="flex items-center gap-2 font-semibold text-xs mb-1 text-amber-800">
                      <AlertTriangle size={15} className="text-amber-600 shrink-0" />
                      First Set Certificate
                    </div>
                    <p className="text-[11px] text-amber-700 leading-normal mb-3">
                      For Certificate Print, you need to set up the Certificate template first.
                    </p>
                    <button
                      type="button"
                      onClick={() => handleTemplateSelection("certificate")}
                      className="w-full py-2 px-3 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white font-medium text-xs shadow-sm transition-all cursor-pointer text-center"
                    >
                      Set Certificate
                    </button>
                  </div>
                )}

                {design && (
                <React.Fragment>
                <AccordionItem
                  id="userInfo"
                  title="Preview Data"
                  icon={Type}
                  openPanels={openPanels}
                  togglePanel={togglePanel}
                >
                  <div className="flex flex-col gap-3">
                    <label className="text-xs font-medium text-gray-600 flex flex-col gap-1">
                      Event Name
                      <textarea
                        className="border border-gray-200 rounded px-3 py-2 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none w-full bg-white text-gray-800"
                        rows={1}
                        value={data.eventName}
                        onChange={(e) =>
                          setData({ ...data, eventName: e.target.value })
                        }
                      />
                    </label>
                    <label className="text-xs font-medium text-gray-600 flex flex-col gap-1">
                      Event Date
                      <textarea
                        className="border border-gray-200 rounded px-3 py-2 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none w-full bg-white text-gray-800"
                        rows={1}
                        value={data.date || ""}
                        onChange={(e) =>
                          setData({ ...data, date: e.target.value })
                        }
                      />
                    </label>
                    <label className="text-xs font-medium text-gray-600 flex flex-col gap-1">
                      Validity Dates
                      <textarea
                        className="border border-gray-200 rounded px-3 py-2 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none w-full bg-white text-gray-800"
                        rows={1}
                        value={data.ticketValidDates || ""}
                        onChange={(e) =>
                          setData({ ...data, ticketValidDates: e.target.value })
                        }
                      />
                    </label>
                    <label className="text-xs font-medium text-gray-600 flex flex-col gap-1">
                      Venue
                      <textarea
                        className="border border-gray-200 rounded px-3 py-2 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none w-full bg-white text-gray-800"
                        rows={1}
                        value={data.venue || ""}
                        onChange={(e) =>
                          setData({ ...data, venue: e.target.value })
                        }
                      />
                    </label>
                    <label className="text-xs font-medium text-gray-600 flex flex-col gap-1">
                      QR Code URL
                      <textarea
                        className="border border-gray-200 rounded px-3 py-2 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none w-full bg-white text-gray-800"
                        rows={1}
                        value={data.qrData}
                        onChange={(e) =>
                          setData({ ...data, qrData: e.target.value })
                        }
                      />
                    </label>

                    {title === "Group Ticket Designer" && (
                      <label className="text-xs font-medium text-gray-600 flex flex-col gap-1">
                        Public Id
                        <textarea
                          className="border border-gray-200 rounded px-3 py-2 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none w-full bg-white text-gray-800"
                          rows={1}
                          value={data.ticketId}
                          onChange={(e) =>
                            setData({ ...data, ticketId: e.target.value })
                          }
                        />
                      </label>
                    )}

                    {title !== "Group Ticket Designer" && (
                      <>
                        <label className="text-xs font-medium text-gray-600 flex flex-col gap-1">
                          Public Id
                          <textarea
                            className="border border-gray-200 rounded px-3 py-2 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none w-full bg-white text-gray-800"
                            rows={1}
                            value={data.ticketId}
                            onChange={(e) =>
                              setData({ ...data, ticketId: e.target.value })
                            }
                          />
                        </label>
                        <label className="text-xs font-medium text-gray-600 flex flex-col gap-1">
                          Ticket Name
                          <textarea
                            className="border border-gray-200 rounded px-3 py-2 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none w-full bg-white text-gray-800"
                            rows={1}
                            value={data.ticketName}
                            onChange={(e) =>
                              setData({ ...data, ticketName: e.target.value })
                            }
                          />
                        </label>
                        <label className="text-xs font-medium text-gray-600 flex flex-col gap-1">
                          Attendee Name
                          <textarea
                            className="border border-gray-200 rounded px-3 py-2 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none w-full bg-white text-gray-800"
                            rows={2}
                            value={data.name}
                            onChange={(e) =>
                              setData({ ...data, name: e.target.value })
                            }
                          />
                        </label>

                        <label className="text-xs font-medium text-gray-600 flex flex-col gap-1">
                          Date
                          <textarea
                            className="border border-gray-200 rounded px-3 py-2 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none w-full bg-white text-gray-800"
                            rows={1}
                            value={data.date}
                            onChange={(e) =>
                              setData({ ...data, date: e.target.value })
                            }
                          />
                        </label>
                        <label className="text-xs font-medium text-gray-600 flex flex-col gap-1">
                          Booked By
                          <textarea
                            className="border border-gray-200 rounded px-3 py-2 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none w-full bg-white text-gray-800"
                            rows={1}
                            value={data.bookedBy}
                            onChange={(e) =>
                              setData({ ...data, bookedBy: e.target.value })
                            }
                          />
                        </label>
                        <label className="text-xs font-medium text-gray-600 flex flex-col gap-1">
                          Profile Image URL (Optional)
                          <input
                            className="border border-gray-200 rounded px-3 py-2 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none w-full bg-white text-gray-800"
                            value={data.profile || ""}
                            placeholder="https://..."
                            onChange={(e) =>
                              setData({ ...data, profile: e.target.value })
                            }
                          />
                        </label>
                      </>
                    )}
                  </div>
                </AccordionItem>

                <AccordionItem
                  id="canvasConfig"
                  title="Canvas Dimensions"
                  icon={Layout}
                  openPanels={openPanels}
                  togglePanel={togglePanel}
                >
                  <div className="flex flex-col gap-4">
                    {/* Unit switcher */}
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                        Unit
                      </span>
                      <div className="flex bg-gray-100 rounded-lg p-0.5 gap-0.5">
                        {["px", "in", "cm", "mm"].map((u) => (
                          <button
                            key={u}
                            onClick={() => setCanvasUnit(u)}
                            className={`px-2.5 py-1 text-xs font-semibold rounded-md transition-all ${
                              canvasUnit === u
                                ? "bg-white text-blue-600 shadow-sm"
                                : "text-gray-500 hover:text-gray-700"
                            }`}
                          >
                            {u}
                          </button>
                        ))}
                      </div>
                    </div>

                    <LiveSelect
                      label="Preset Size"
                      value={(() => {
                        const matchIdx = CANVAS_PRESETS.findIndex(
                          (p) =>
                            p.w === design.canvas.width &&
                            p.h === design.canvas.height,
                        );
                        return matchIdx !== -1 ? matchIdx : 0;
                      })()}
                      options={CANVAS_PRESETS.map((p, i) => ({
                        value: i,
                        label: p.label,
                      }))}
                      onChange={(v) => {
                        const p = CANVAS_PRESETS[v];
                        if (p.w) updateCanvas({ width: p.w, height: p.h });
                      }}
                    />

                    <div className="grid grid-cols-2 gap-3">
                      <NumberInput
                        label={`Width (${canvasUnit})`}
                        step={
                          canvasUnit === "px"
                            ? 1
                            : canvasUnit === "mm"
                              ? 0.1
                              : 0.01
                        }
                        min={canvasUnit === "px" ? 100 : toUnit(100)}
                        max={canvasUnit === "px" ? 800 : toUnit(800)}
                        value={
                          isEditingCanvasWidth
                            ? canvasWidthDraft
                            : toUnit(design.canvas.width)
                        }
                        onBlur={(e) => {
                          setIsEditingCanvasWidth(false);
                          commitCanvasDimension("width", e.target.value);
                        }}
                        onChange={(v) => {
                          setCanvasWidthDraft(String(v));
                          updateCanvasDimensionLive("width", v);
                          if (!isEditingCanvasWidth)
                            setIsEditingCanvasWidth(true);
                        }}
                      />
                      <NumberInput
                        label={`Height (${canvasUnit})`}
                        step={
                          canvasUnit === "px"
                            ? 1
                            : canvasUnit === "mm"
                              ? 0.1
                              : 0.01
                        }
                        min={canvasUnit === "px" ? 100 : toUnit(100)}
                        max={canvasUnit === "px" ? 1200 : toUnit(1200)}
                        value={
                          isEditingCanvasHeight
                            ? canvasHeightDraft
                            : toUnit(design.canvas.height)
                        }
                        onBlur={(e) => {
                          setIsEditingCanvasHeight(false);
                          commitCanvasDimension("height", e.target.value);
                        }}
                        onChange={(v) => {
                          setCanvasHeightDraft(String(v));
                          updateCanvasDimensionLive("height", v);
                          if (!isEditingCanvasHeight)
                            setIsEditingCanvasHeight(true);
                        }}
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <NumberInput
                        label="Padding (px)"
                        value={design.canvas.padding}
                        min={0}
                        max={100}
                        onChange={(v) => updateCanvas({ padding: v })}
                      />
                      <NumberInput
                        label="All Corners (px)"
                        value={canvasCornerRadii.all}
                        min={0}
                        max={150}
                        onChange={(v) => updateCanvas({ borderRadius: v })}
                      />
                      <NumberInput
                        label="Border Width (px)"
                        value={design.canvas.borderWidth}
                        min={1}
                        max={20}
                        onChange={(v) => updateCanvas({ borderWidth: v })}
                      />
                    </div>
                    <div className="rounded-2xl border border-gray-200 bg-white p-4 space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-gray-500">
                            Corner Radius
                          </div>
                          <div className="text-[11px] text-gray-400 mt-1">
                            Fine tune each ticket corner or change all at once.
                          </div>
                        </div>
                        <div
                          className="grid grid-cols-2 gap-1 rounded-xl border border-gray-200 bg-gray-50 p-2"
                          style={{
                            width: 68,
                            height: 68,
                            borderTopLeftRadius: canvasCornerRadii.topLeft,
                            borderTopRightRadius: canvasCornerRadii.topRight,
                            borderBottomRightRadius:
                              canvasCornerRadii.bottomRight,
                            borderBottomLeftRadius:
                              canvasCornerRadii.bottomLeft,
                          }}
                        >
                          <div className="rounded-md bg-indigo-100/80"></div>
                          <div className="rounded-md bg-indigo-100/80"></div>
                          <div className="rounded-md bg-indigo-100/80"></div>
                          <div className="rounded-md bg-indigo-100/80"></div>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <NumberInput
                          label="Top Left"
                          value={canvasCornerRadii.topLeft}
                          min={0}
                          max={150}
                          onChange={(v) =>
                            updateCanvas({ borderTopLeftRadius: v })
                          }
                        />
                        <NumberInput
                          label="Top Right"
                          value={canvasCornerRadii.topRight}
                          min={0}
                          max={150}
                          onChange={(v) =>
                            updateCanvas({ borderTopRightRadius: v })
                          }
                        />
                        <NumberInput
                          label="Bottom Left"
                          value={canvasCornerRadii.bottomLeft}
                          min={0}
                          max={150}
                          onChange={(v) =>
                            updateCanvas({ borderBottomLeftRadius: v })
                          }
                        />
                        <NumberInput
                          label="Bottom Right"
                          value={canvasCornerRadii.bottomRight}
                          min={0}
                          max={150}
                          onChange={(v) =>
                            updateCanvas({ borderBottomRightRadius: v })
                          }
                        />
                      </div>
                    </div>
                    <div className="rounded-2xl border border-gray-200 bg-white p-4 space-y-4">
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-gray-500">
                            Canvas Fill
                          </div>
                          <div className="text-[11px] text-gray-400 mt-1">
                            Use a solid color or build a richer gradient behind the ticket.
                          </div>
                        </div>
                        <div
                          className="h-12 w-24 rounded-xl border border-gray-200 shadow-inner"
                          style={{
                            background:
                              canvasBackgroundMode === "gradient"
                                ? canvasGradientPreview
                                : design.canvas.background,
                          }}
                        ></div>
                      </div>
                      <GradientEditor
                        mode={canvasBackgroundMode}
                        onModeChange={(v) => updateCanvas({ backgroundMode: v })}
                        angle={Number(design.canvas.backgroundGradientAngle ?? 135)}
                        onAngleChange={(v) =>
                          updateCanvas({ backgroundGradientAngle: v })
                        }
                        stops={design.canvas.backgroundGradientStops || [
                          {
                            id: "canvas-stop-1",
                            position: 0,
                            color:
                              design.canvas.backgroundGradientFrom ||
                              design.canvas.background ||
                              "#ffffff",
                            opacity: 100,
                          },
                          {
                            id: "canvas-stop-2",
                            position: 100,
                            color:
                              design.canvas.backgroundGradientTo || "#dbeafe",
                            opacity: 100,
                          },
                        ]}
                        onStopsChange={(stops) =>
                          updateCanvas({
                            backgroundGradientStops: stops,
                            backgroundGradientFrom: stops[0]?.color || "#ffffff",
                            backgroundGradientTo:
                              stops[stops.length - 1]?.color || "#dbeafe",
                          })
                        }
                      />
                      {canvasBackgroundMode !== "gradient" && (
                        <ColorInput
                          label="Background Color"
                          value={design.canvas.background}
                          onChange={(v) => updateCanvas({ background: v })}
                          canBeTransparent={true}
                        />
                      )}
                    </div>
                    <ColorInput
                      label="Border Color"
                      value={design.canvas.borderColor}
                      onChange={(v) => updateCanvas({ borderColor: v })}
                    />
                  </div>
                </AccordionItem>

                <AccordionItem
                  id="background"
                  title="Background Image"
                  icon={ImageIcon}
                  openPanels={openPanels}
                  togglePanel={togglePanel}
                >
                  <div className="flex flex-col gap-4">
                    <div className="flex items-center justify-center w-full">
                      <label className="flex flex-col items-center justify-center w-full h-24 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100">
                        <div className="flex flex-col items-center justify-center pt-5 pb-6">
                          <ImageIcon size={20} className="text-gray-400 mb-2" />
                          <p className="mb-1 text-xs text-gray-500">
                            <span className="font-semibold">
                              Click to upload
                            </span>
                          </p>
                        </div>
                        <input
                          type="file"
                          className="hidden"
                          accept="image/png,image/jpeg"
                          onChange={handleBackgroundImageChange}
                        />
                      </label>
                    </div>

                    {design.canvas.backgroundImage && (
                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-semibold text-gray-600">
                            Preview
                          </span>
                          <button
                            onClick={handleRemoveBackgroundImage}
                            className="text-[10px] text-red-500 hover:text-red-700 bg-red-50 px-2 py-0.5 rounded"
                          >
                            Remove
                          </button>
                        </div>
                        <img
                          src={design.canvas.backgroundImage}
                          alt="Background"
                          className="w-full h-auto max-h-24 object-contain rounded border border-gray-200 bg-white mb-3"
                        />

                        {(tag === "forPrint" || tag === "badge" || tag === "forCertificatePrint") && title !== "Group Ticket Designer" && (
                          <label className="mb-3 flex items-center justify-between gap-3 rounded-md border border-gray-200 bg-white px-3 py-2 text-xs font-medium text-gray-700">
                            <span>Print Background Image</span>
                            <input
                              type="checkbox"
                              className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                              checked={design.canvas.printBackgroundImage !== false}
                              onChange={(e) =>
                                updateCanvas({
                                  printBackgroundImage: e.target.checked,
                                })
                              }
                            />
                          </label>
                        )}

                        <div className="text-xs font-medium text-gray-600 flex flex-col gap-1">
                          <div className="flex justify-between flex-1">
                            <span>Opacity (%)</span>
                            <span>
                              {Math.round(
                                (design.canvas.backgroundImageOpacity ?? 0.5) *
                                  100,
                              )}
                              %
                            </span>
                          </div>
                          <input
                            type="range"
                            min="0"
                            max="100"
                            className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                            value={Math.round(
                              (design.canvas.backgroundImageOpacity ?? 0.5) *
                                100,
                            )}
                            onChange={(e) =>
                              updateCanvas({
                                backgroundImageOpacity:
                                  parseInt(e.target.value) / 100,
                              })
                            }
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </AccordionItem>

                <AccordionItem
                  id="header"
                  title="Header Area"
                  icon={Layout}
                  openPanels={openPanels}
                  togglePanel={togglePanel}
                >
                  <div className="flex flex-col gap-4">
                    <label className="flex items-center gap-2 text-sm font-medium text-gray-700 cursor-pointer p-2 hover:bg-gray-50 rounded-md border border-gray-200 bg-white">
                      <input
                        type="checkbox"
                        className="rounded text-indigo-600 w-4 h-4 cursor-pointer focus:ring-indigo-500"
                        checked={design.header.enabled}
                        onChange={(e) =>
                          updateHeader({ enabled: e.target.checked })
                        }
                      />
                      Enable Header Banner
                    </label>

                    {design.header.enabled && (
                      <>
                        <label className="text-xs font-medium text-gray-600 flex flex-col gap-1">
                          Header Text
                          <input
                            className="border border-gray-200 rounded px-3 py-2 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none w-full bg-white text-gray-800"
                            value={design.header.text}
                            onChange={(e) =>
                              updateHeader({ text: e.target.value })
                            }
                          />
                        </label>
                        <div className="grid grid-cols-2 gap-3">
                          <NumberInput
                            label="Height"
                            value={design.header.height}
                            min={20}
                            max={200}
                            onChange={(v) => updateHeader({ height: v })}
                          />
                          <NumberInput
                            label="Font Size"
                            value={design.header.fontSize}
                            min={8}
                            max={48}
                            onChange={(v) => updateHeader({ fontSize: v })}
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <ColorInput
                            label="Background Color"
                            value={design.header.bg}
                            onChange={(v) => updateHeader({ bg: v })}
                            canBeTransparent={true}
                          />
                          <ColorInput
                            label="Text Color"
                            value={design.header.color}
                            onChange={(v) => updateHeader({ color: v })}
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-3 pb-2 items-end">
                          <LiveSelect
                            label="Font Family"
                            value={design.header.fontFamily}
                            options={FONT_OPTIONS.map((f) => ({
                              value: f,
                              label: f,
                            }))}
                            isFont={true}
                            onChange={(v) => updateHeader({ fontFamily: v })}
                          />
                          <LiveSelect
                            label="Text Align"
                            value={design.header.align}
                            options={[
                              { value: "left", label: "Left" },
                              { value: "center", label: "Center" },
                              { value: "right", label: "Right" },
                            ]}
                            onChange={(v) => updateHeader({ align: v })}
                          />
                        </div>
                      </>
                    )}
                  </div>
                </AccordionItem>
                </React.Fragment>
                )}
              </div>
            )}

            {/* PROPERTIES TAB */}
            {!readOnly && design && activeTab === "properties" && (
              <div className="flex flex-col p-4">
                {selectedSection === "canvas" ||
                selectedSection === "background" ||
                selectedSection === "header" ? (
                  <div className="flex flex-col items-center justify-center p-8 text-center text-gray-500 border border-dashed border-gray-200 bg-white rounded-lg mt-4">
                    <Settings size={32} className="text-gray-300 mb-3" />
                    <p className="text-sm font-medium text-gray-700">
                      Canvas Selected
                    </p>
                    <p className="text-xs mt-1">
                      Please use the Design tab to adjust canvas settings, or
                      select a specific layer from the Layers tab.
                    </p>
                    <button
                      className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded border border-indigo-600 text-xs font-medium hover:bg-indigo-700 transition-colors cursor-pointer"
                      onClick={() => setActiveTab("design")}
                    >
                      Go to Design Tab
                    </button>
                  </div>
                ) : !design.sections[selectedSection] ? (
                  <div className="flex flex-col items-center justify-center p-8 text-center text-gray-500 border border-dashed border-gray-200 bg-white rounded-lg mt-4">
                    <p className="text-sm">
                      Select a layer to edit its properties.
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="mb-4 flex items-center gap-2 pb-3 border-b border-gray-100">
                      <div className="bg-indigo-100 text-indigo-700 text-[10px] font-bold px-2 py-0.5 rounded tracking-wider uppercase">
                        {selectedSection === "ticketName"
                          ? "text"
                          : design.sections[selectedSection].type}
                      </div>
                      <h3 className="font-semibold text-gray-800 text-sm m-0 capitalize flex-1">
                        {selectedSection === "ticketName"
                          ? "Ticket Class"
                          : selectedSection === "ticketId" &&
                              title === "Group Ticket Designer"
                            ? "Public Id"
                            : design.sections[selectedSection]?.label ||
                              selectedSection
                                .replace(/([A-Z])/g, " $1")
                                .replace(/^./, (str) => str.toUpperCase())}
                      </h3>
                    </div>

                    {/* Render actual properties component locally wrapped */}
                    <SectionControls
                      id={selectedSection}
                      canvas={design.canvas}
                      def={design.sections[selectedSection]}
                      measuredSize={sectionMeasurements[selectedSection]}
                      onChange={(next) => updateSection(selectedSection, next)}
                      hideHeadingControls={hidePairHeading}
                    />

                    {/* Common properties for any layer */}
                    <div className="mt-4 pt-4 border-t border-gray-100 flex flex-col gap-3">
                      <label className="text-xs font-medium text-gray-600 flex flex-col gap-1">
                        Rotation angle (
                        {design.sections[selectedSection].rotation || 0}°)
                        <input
                          type="range"
                          min="0"
                          max="360"
                          className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                          value={design.sections[selectedSection].rotation || 0}
                          onChange={(e) =>
                            updateSection(selectedSection, {
                              rotation: parseInt(e.target.value),
                            })
                          }
                        />
                      </label>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* LAYERS TAB */}
            {!readOnly && design && activeTab === "layers" && (
              <div className="flex flex-col">
                <div className="text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3 bg-gray-50 border-b border-gray-100 mb-2 flex justify-between items-center z-10 relative">
                  <div className="flex items-center gap-3">
                    <label
                      className="flex items-center gap-2 cursor-pointer group"
                      title="Toggle all layers"
                    >
                      <div className="relative inline-flex items-center">
                        <input
                          type="checkbox"
                          className="sr-only peer"
                          checked={design.sectionsOrder.every(
                            (id) => !!design.sections[id]?.enabled,
                          )}
                          onChange={(e) => toggleAllLayers(e.target.checked)}
                        />
                        <div className="w-7 h-4 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-3 after:w-3 after:transition-all peer-checked:bg-indigo-600"></div>
                      </div>
                      <span className="text-[10px] text-gray-400 group-hover:text-gray-600 transition-colors">
                        Show All
                      </span>
                    </label>
                    <div className="w-[1px] h-3 bg-gray-300 mx-1"></div>
                    <div className="relative">
                      <button
                        onClick={() => setShowAddMenu(!showAddMenu)}
                        className="p-1 hover:bg-gray-200 rounded text-gray-600 flex items-center gap-1"
                        title="Add Layer"
                      >
                        <Plus size={16} />
                      </button>
                      {showAddMenu && (
                        <div className="absolute right-0 top-full mt-1 w-36 bg-white rounded shadow-lg border border-gray-200 py-1 z-50 normal-case tracking-normal">
                          <button
                            className="w-full text-left px-3 py-2 text-xs hover:bg-gray-50 flex items-center gap-2"
                            onClick={() => handleAddLayer("text")}
                          >
                            <Type size={14} /> Text
                          </button>
                          <button
                            className="w-full text-left px-3 py-2 text-xs hover:bg-gray-50 flex items-center gap-2"
                            onClick={() => handleAddLayer("image")}
                          >
                            <ImageIcon size={14} /> Image
                          </button>
                          <button
                            className="w-full text-left px-3 py-2 text-xs hover:bg-gray-50 flex items-center gap-2"
                            onClick={() => handleAddLayer("shape")}
                          >
                            <Square size={14} /> Shape
                          </button>
                          <button
                            className="w-full text-left px-3 py-2 text-xs hover:bg-gray-50 flex items-center gap-2"
                            onClick={() => handleAddLayer("divider")}
                          >
                            <Minus size={14} /> Divider
                          </button>
                          {canAddFormFields && (
                            <button
                              className="w-full text-left px-3 py-2 text-xs hover:bg-gray-50 flex items-center gap-2 border-t border-gray-100"
                              onClick={() => {
                                setShowAddMenu(false);
                                setShowFormFieldPicker(true);
                              }}
                            >
                              <Type size={14} /> Form Field
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="px-2 pb-4">
                  <DragDropContext onDragEnd={onDragEnd}>
                    <Droppable droppableId="sections">
                      {(provided) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                          className="flex flex-col gap-1"
                        >
                          {design.sectionsOrder
                            .filter((id) => id !== "email")
                            .map((secId, idx) => {
                              const def = design.sections[secId];
                              if (!def) return null;
                              const isSelected = selectedSection === secId;

                              return (
                                <Draggable
                                  draggableId={secId}
                                  index={idx}
                                  key={secId}
                                >
                                  {(p) => (
                                    <div
                                      ref={p.innerRef}
                                      {...p.draggableProps}
                                      className={`flex items-center justify-between p-3 rounded-md border transition-all hover:bg-gray-50 ${
                                        isSelected
                                          ? "border-indigo-400 bg-indigo-50/50 shadow-[0_0_0_1px_rgba(99,102,241,0.3)]"
                                          : "border-gray-200 bg-white"
                                      }`}
                                      onClick={() => setSelectedSection(secId)}
                                    >
                                      <div className="flex items-center gap-3 overflow-hidden">
                                        <span
                                          className="text-gray-400 cursor-grab active:cursor-grabbing"
                                          {...p.dragHandleProps}
                                        >
                                          <svg
                                            width="14"
                                            height="14"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                          >
                                            <circle
                                              cx="9"
                                              cy="12"
                                              r="1"
                                            ></circle>
                                            <circle
                                              cx="9"
                                              cy="5"
                                              r="1"
                                            ></circle>
                                            <circle
                                              cx="9"
                                              cy="19"
                                              r="1"
                                            ></circle>
                                            <circle
                                              cx="15"
                                              cy="12"
                                              r="1"
                                            ></circle>
                                            <circle
                                              cx="15"
                                              cy="5"
                                              r="1"
                                            ></circle>
                                            <circle
                                              cx="15"
                                              cy="19"
                                              r="1"
                                            ></circle>
                                          </svg>
                                        </span>
                                        <div className="flex flex-col truncate">
                                          <span
                                            className={`text-[13px] font-semibold truncate ${isSelected ? "text-indigo-700" : "text-gray-700"}`}
                                          >
                                            {secId === "ticketName"
                                              ? "Ticket Class"
                                              : secId === "ticketId" &&
                                                  title ===
                                                    "Group Ticket Designer"
                                                ? "Public Id"
                                                : def.label ||
                                                  secId
                                                    .replace(/([A-Z])/g, " $1")
                                                    .replace(/^./, (str) =>
                                                      str.toUpperCase(),
                                                    )}
                                          </span>
                                          <span className="text-[10px] text-gray-400 capitalize">
                                            {def.type} Layer
                                          </span>
                                        </div>
                                      </div>

                                      <div className="flex flex-col items-center ml-2 shrink-0 gap-2">
                                        <label className="relative inline-flex items-center cursor-pointer">
                                          <input
                                            type="checkbox"
                                            className="sr-only peer"
                                            checked={!!def.enabled}
                                            onChange={(e) =>
                                              toggleSection(
                                                secId,
                                                e.target.checked,
                                              )
                                            }
                                            onClick={(e) => e.stopPropagation()}
                                          />
                                          <div className="w-7 h-4 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-3 after:w-3 after:transition-all peer-checked:bg-indigo-600"></div>
                                        </label>
                                        {(secId.startsWith("custom_") ||
                                          secId.startsWith("form_field_")) && (
                                          <button
                                            className="text-gray-400 hover:text-red-500 transition-colors p-1"
                                            onClick={(e) =>
                                              handleRemoveLayer(secId, e)
                                            }
                                            title="Remove custom layer"
                                          >
                                            <Trash2 size={14} />
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                </Draggable>
                              );
                            })}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </DragDropContext>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {showFormFieldPicker && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4 bg-black/40">
          <div className="w-full max-w-md rounded-xl bg-white shadow-2xl border border-gray-200 overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
              <div>
                <h3 className="text-sm font-semibold text-gray-900 m-0">
                  Add Form Field
                </h3>
                <p className="text-xs text-gray-500 m-0 mt-0.5">
                  Text, Number, Email, and Date fields only
                </p>
              </div>
              <button
                type="button"
                className="p-1 rounded text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                onClick={() => {
                  setShowFormFieldPicker(false);
                  setFormFieldSearch("");
                }}
              >
                <X size={18} />
              </button>
            </div>
            <div className="p-4">
              <input
                autoFocus
                type="search"
                value={formFieldSearch}
                onChange={(e) => setFormFieldSearch(e.target.value)}
                placeholder="Search form fields"
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100"
              />
              <div className="mt-3 max-h-72 overflow-y-auto">
                {formFieldsLoading ? (
                  <div className="py-8 text-center text-sm text-gray-400">
                    Loading fields...
                  </div>
                ) : filteredFormFields.length === 0 ? (
                  <div className="py-8 text-center text-sm text-gray-400">
                    No matching form fields found.
                  </div>
                ) : (
                  <div className="flex flex-col gap-1">
                    {filteredFormFields.map((field) => {
                      const fieldName = getFieldName(field);
                      const fieldType = getFieldType(field);
                      return (
                        <button
                          key={`${field.id || field.field_key || fieldName}-${fieldType}`}
                          type="button"
                          className="w-full rounded-lg px-3 py-2 text-left hover:bg-indigo-50 transition-colors"
                          onClick={() => handleAddFormFieldLayer(field)}
                        >
                          <span className="block text-sm font-medium text-gray-800 truncate">
                            {fieldName}
                          </span>
                          <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                            {fieldType}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <ResetTemplateModal
        isOpen={showResetModal}
        onClose={() => setShowResetModal(false)}
        onResetToDefault={() => handleReset("default")}
        onResetToLastSave={() => handleReset("lastSaved")}
        onCopyFromClass={(classId) => handleReset("copyFromClass", classId)}
        isSaving={isSaving}
        showCopyOption={title !== "Group Ticket Designer"}
        otherTicketClasses={otherTicketClasses}
        loadingClasses={loadingClasses}
        tag={tag}
      />
      <TemplateSwitchModal
        isOpen={showTemplateChangeModal}
        onClose={closeTemplateSwitchModal}
        onSaveAndSwitch={handleSaveAndSwitch}
        onDiscardAndSwitch={handleDiscardAndSwitch}
        currentTemplateLabel={currentTemplateLabel}
        nextTemplateLabel={pendingTemplateLabel}
        isSaving={isSaving}
      />
    </div>
  );
}
